// OWNER: platform module (offline area). The offline outbox and its replay engine (NFR "Offline", ADR 0004).
//
// Writes made while offline are stored in IndexedDB (outboxStore.ts) and replayed when the device is back
// online: on the `online` event, on app start, when the page becomes visible again, when the service worker
// relays a Background Sync event, and on a backoff timer after temporary errors.
//
// Replay rules
//  - Items are sent in the order they were made, one at a time per workspace, with the normal session cookie
//    and CSRF header (the shared `api` client), plus `Idempotency-Key`, `x-client-updated-at` and
//    `x-offline-replay` headers (see packages/types/src/platform/offline.ts for the server convention).
//  - 2xx → removed. A 2xx body with `conflict: true` means the server kept a newer version and stored this
//    change in its history → shown as a conflict (content dropped).
//  - Network failure → stop and wait for the next trigger (does not count as an attempt).
//  - 5xx / 408 / 429 / CSRF → exponential backoff with jitter; later items of that workspace wait (order).
//  - 401 → stop everything until the user signs in again (`authPaused`).
//  - 402 (read-only) → items stay queued as "blocked" and are retried on the next sync.
//  - 409 / 412 → conflict: recorded for review in the banner, content dropped, the next item continues.
//  - Other 4xx (400/403/404…) → "failed": kept for the user to retry or discard; the next item continues.
//  - After items are sent, ['w', wsId, module] queries are invalidated (see OfflineBanner).
import { OFFLINE_HEADERS } from '@seventide/types';
import { ApiError, api } from '../../../lib/api';
import { createStore, type Store } from '../../../lib/store';
import { readStorage, writeStorage } from '../../../lib/storage';
import { createDefaultStore, type OutboxStore } from './outboxStore';
import type { OutboxError, OutboxItem, OutboxMethod, OutboxState } from './types';

export const OUTBOX_SYNC_TAG = 'seventide-outbox';
export const MAX_ATTEMPTS = 12;
const DAY = 86_400_000;
/** Conflict notices (no content) are kept for a week; anything older than 30 days is discarded. */
const CONFLICT_TTL = 7 * DAY;
const ITEM_TTL = 30 * DAY;
const CLOCK_KEY = 'st.clockOffsetMs';

export function newClientId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export type ReplayErrorKind = 'offline' | 'auth' | 'readonly' | 'conflict' | 'retry' | 'failed';

/** How the replay treats an error from the server (see header). */
export function classifyReplayError(err: unknown): ReplayErrorKind {
  if (!(err instanceof ApiError)) return 'retry';
  if (err.status === 0 || err.code === 'network_error') return 'offline';
  if (err.status === 401) return 'auth';
  if (err.status === 402) return 'readonly';
  if (err.status === 409 || err.status === 412) return 'conflict';
  if (err.code === 'csrf' || err.status === 408 || err.status === 425 || err.status === 429 || err.status >= 500)
    return 'retry';
  return 'failed';
}

/** Exponential backoff with ±20% jitter: 5 s, 10 s, 20 s … capped at 10 minutes. */
export function backoffDelay(attempts: number, random: () => number = Math.random, baseMs = 5_000, maxMs = 600_000) {
  const raw = Math.min(baseMs * 2 ** Math.max(0, attempts - 1), maxMs);
  return Math.round(raw * (0.8 + 0.4 * random()));
}

export function replayHeaders(item: Pick<OutboxItem, 'clientId' | 'clientUpdatedAt' | 'baseUpdatedAt'>) {
  const h: Record<string, string> = {
    [OFFLINE_HEADERS.idempotencyKey]: item.clientId,
    [OFFLINE_HEADERS.clientUpdatedAt]: item.clientUpdatedAt,
    [OFFLINE_HEADERS.replay]: '1',
  };
  if (item.baseUpdatedAt) h[OFFLINE_HEADERS.baseUpdatedAt] = item.baseUpdatedAt;
  return h;
}

const isConflictBody = (res: unknown) =>
  Boolean(res && typeof res === 'object' && (res as { conflict?: unknown }).conflict === true);

const toOutboxError = (err: unknown): OutboxError =>
  err instanceof ApiError
    ? { status: err.status, code: String(err.code), message: err.message }
    : { status: 0, code: 'unknown', message: err instanceof Error ? err.message : 'Unknown error' };

export interface EnqueueInput {
  workspaceId: string;
  module: string;
  entityType: string;
  method: OutboxMethod;
  path: string;
  body?: unknown;
  /** Idempotency key (defaults to entityId for creates, else a new UUID). */
  clientId?: string;
  entityId?: string | null;
  /** Device time of the change (defaults to the clock-corrected now). */
  clientUpdatedAt?: string;
  baseUpdatedAt?: string | null;
}

export interface ReplayResult {
  sent: number;
  conflicts: number;
  failed: number;
  blocked: number;
  /** Items still waiting after this run. */
  remaining: number;
  stoppedBy: 'offline' | 'auth' | null;
}

export interface OutboxOptions {
  store?: OutboxStore;
  now?: () => number;
  random?: () => number;
  /** Sends one item. Default: the shared `api` client (cookie + CSRF) with the replay headers. */
  send?: (item: OutboxItem) => Promise<unknown>;
  isOnline?: () => boolean;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /** Ask the service worker for a Background Sync (default: navigator.serviceWorker.ready.sync.register). */
  requestBackgroundSync?: () => void;
}

const defaultSend = (item: OutboxItem) =>
  api.request<unknown>(item.path, {
    method: item.method,
    body: item.body,
    headers: replayHeaders(item),
    redirectOn401: false,
  });

const defaultOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);

function defaultBackgroundSync(): void {
  try {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    if (!sw?.ready) return;
    void sw.ready
      .then((reg) => {
        const sync = (reg as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }).sync;
        return sync?.register(OUTBOX_SYNC_TAG);
      })
      .catch(() => {});
  } catch {
    /* Background Sync is optional */
  }
}

type SyncedListener = (e: { workspaceId: string; modules: string[] }) => void;

export class Outbox {
  readonly state: Store<OutboxState>;
  private readonly store: OutboxStore;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly send: (item: OutboxItem) => Promise<unknown>;
  private readonly isOnline: () => boolean;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;
  private readonly bgSync: () => void;
  private userId: string | null = null;
  private running: Promise<ReplayResult> | null = null;
  private rerun = false;
  private timer: unknown = null;
  private lastSeq = 0;
  private readonly listeners = new Set<SyncedListener>();
  private clockOffsetMs = 0;

  constructor(opts: OutboxOptions = {}) {
    this.store = opts.store ?? createDefaultStore();
    this.now = opts.now ?? (() => Date.now());
    this.random = opts.random ?? Math.random;
    this.send = opts.send ?? defaultSend;
    this.isOnline = opts.isOnline ?? defaultOnline;
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
    this.bgSync = opts.requestBackgroundSync ?? defaultBackgroundSync;
    this.state = createStore<OutboxState>({
      items: [],
      syncing: false,
      authPaused: false,
      online: this.isOnline(),
      lastSyncedAt: null,
      lastSyncedCount: 0,
    });
    const stored = Number(readStorage(CLOCK_KEY));
    if (Number.isFinite(stored) && Math.abs(stored) < 7 * DAY) this.clockOffsetMs = stored;
  }

  // ------------------------------------------------------------------ clock (LWW uses device time)

  /** Device time corrected by the last known server clock offset (ISO). */
  deviceNowIso(): string {
    return new Date(this.now() + this.clockOffsetMs).toISOString();
  }

  /** Records the server clock (from GET /platform/sync/status) to correct `clientUpdatedAt` for drift. */
  setServerTime(serverTimeIso: string, requestStartedAt: number, responseAt: number = this.now()): void {
    const server = Date.parse(serverTimeIso);
    if (!Number.isFinite(server)) return;
    const local = requestStartedAt + (responseAt - requestStartedAt) / 2;
    const offset = Math.round(server - local);
    // Ignore sub-second noise; keep the stored value stable.
    this.clockOffsetMs = Math.abs(offset) < 1000 ? 0 : offset;
    writeStorage(CLOCK_KEY, String(this.clockOffsetMs));
  }

  // ------------------------------------------------------------------ user / connectivity

  /** The signed-in user. Items are replayed and listed only for their own user. */
  setUser(userId: string | null): void {
    if (this.userId === userId) return;
    this.userId = userId;
    void this.refresh();
  }

  get currentUserId(): string | null {
    return this.userId;
  }

  setOnline(online: boolean): void {
    this.state.set((s) => (s.online === online ? s : { ...s, online }));
  }

  onSynced(listener: SyncedListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // ------------------------------------------------------------------ reading

  /** Reloads items for the current user from storage (and prunes expired ones). */
  async refresh(): Promise<OutboxItem[]> {
    let all: OutboxItem[];
    try {
      all = await this.store.all();
    } catch {
      all = [];
    }
    const now = this.now();
    const keep: OutboxItem[] = [];
    for (const item of all) {
      const age = now - Date.parse(item.createdAt);
      if (age > ITEM_TTL || (item.status === 'conflict' && age > CONFLICT_TTL)) {
        await this.store.delete(item.id).catch(() => {});
        continue;
      }
      this.lastSeq = Math.max(this.lastSeq, item.seq);
      if (item.userId === this.userId || item.userId === null) keep.push(item);
    }
    keep.sort((a, b) => a.seq - b.seq);
    this.state.set((s) => ({ ...s, items: keep }));
    return keep;
  }

  /** Is a create/edit for this record still waiting? (Later edits must queue behind it to keep order.) */
  hasPendingFor(workspaceId: string, entityId: string | null | undefined): boolean {
    if (!entityId) return false;
    return this.state
      .get()
      .items.some(
        (i) =>
          i.workspaceId === workspaceId &&
          (i.status === 'pending' || i.status === 'blocked') &&
          (i.entityId === entityId || i.path.includes(entityId)),
      );
  }

  // ------------------------------------------------------------------ writing

  async enqueue(input: EnqueueInput): Promise<OutboxItem> {
    const nowMs = this.now();
    this.lastSeq = Math.max(nowMs, this.lastSeq + 1);
    const entityId = input.entityId ?? null;
    const item: OutboxItem = {
      id: newClientId(),
      userId: this.userId,
      workspaceId: input.workspaceId,
      module: input.module,
      entityType: input.entityType,
      entityId,
      method: input.method,
      path: input.path,
      body: input.body,
      clientId: input.clientId ?? (input.method === 'POST' && entityId ? entityId : newClientId()),
      clientUpdatedAt: input.clientUpdatedAt ?? this.deviceNowIso(),
      baseUpdatedAt: input.baseUpdatedAt ?? null,
      createdAt: new Date(nowMs).toISOString(),
      seq: this.lastSeq,
      attempts: 0,
      nextAttemptAt: null,
      status: 'pending',
      lastError: null,
    };
    await this.store.put(item);
    await this.refresh();
    this.bgSync();
    // We may be "online" with a flaky connection: try again shortly without waiting for an event.
    this.schedule(backoffDelay(1, this.random));
    return item;
  }

  /** Puts a failed/blocked/conflict item back in the queue and syncs now. */
  async retry(id: string): Promise<void> {
    const item = await this.store.get(id);
    if (!item) return;
    if (item.status === 'conflict' && item.body === undefined && item.method !== 'DELETE') {
      // Content was dropped on conflict — nothing to resend.
      return;
    }
    await this.store.put({ ...item, status: 'pending', attempts: 0, nextAttemptAt: null, lastError: null });
    await this.refresh();
    await this.replay({ force: true });
  }

  /** Removes an item (the change is not sent). */
  async discard(id: string): Promise<void> {
    await this.store.delete(id);
    await this.refresh();
  }

  /** Sign-out / account switch: removes every item for every user on this device. */
  async clearAll(): Promise<void> {
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = null;
    await this.store.clear().catch(() => {});
    this.userId = null;
    this.state.set((s) => ({ ...s, items: [], authPaused: false, syncing: false, lastSyncedAt: null, lastSyncedCount: 0 }));
  }

  // ------------------------------------------------------------------ replay

  /**
   * Sends waiting items (single-flight: a call during a run schedules one more run afterwards).
   * `force` ignores the backoff timers (the user pressed "Send now").
   */
  replay(opts: { force?: boolean } = {}): Promise<ReplayResult> {
    if (this.running) {
      this.rerun = true;
      return this.running;
    }
    this.running = this.run(Boolean(opts.force)).finally(() => {
      this.running = null;
      if (this.rerun) {
        this.rerun = false;
        void this.replay();
      }
    });
    return this.running;
  }

  private schedule(delayMs: number): void {
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = this.setTimer(() => {
      this.timer = null;
      void this.replay();
    }, Math.max(0, delayMs));
  }

  private scheduleNextRetry(items: OutboxItem[]): void {
    const now = this.now();
    let next = Infinity;
    // Only the first waiting item of each workspace matters: later ones are sent after it (order).
    const seen = new Set<string>();
    for (const i of items) {
      if (i.status !== 'pending' || seen.has(i.workspaceId)) continue;
      seen.add(i.workspaceId);
      // No retry time (e.g. added during the run): try again after the base delay, never in a tight loop.
      const at = i.nextAttemptAt ? Date.parse(i.nextAttemptAt) : now + backoffDelay(1, this.random);
      next = Math.min(next, at);
    }
    if (next === Infinity) {
      if (this.timer !== null) this.clearTimer(this.timer);
      this.timer = null;
      return;
    }
    this.schedule(next - now);
  }

  private async run(force: boolean): Promise<ReplayResult> {
    const result: ReplayResult = { sent: 0, conflicts: 0, failed: 0, blocked: 0, remaining: 0, stoppedBy: null };
    const online = this.isOnline();
    this.setOnline(online);
    if (!online) {
      result.stoppedBy = 'offline';
      result.remaining = (await this.refresh()).filter((i) => i.status === 'pending' || i.status === 'blocked').length;
      return result;
    }
    const items = await this.refresh();
    const queue = items.filter((i) => i.status === 'pending' || i.status === 'blocked');
    if (!queue.length) {
      this.state.set((s) => ({ ...s, authPaused: false }));
      return result;
    }

    this.state.set((s) => ({ ...s, syncing: true, authPaused: false }));
    const byWorkspace = new Map<string, OutboxItem[]>();
    for (const i of queue) byWorkspace.set(i.workspaceId, [...(byWorkspace.get(i.workspaceId) ?? []), i]);
    const synced = new Map<string, Set<string>>();
    const markSynced = (i: OutboxItem) => synced.set(i.workspaceId, (synced.get(i.workspaceId) ?? new Set()).add(i.module));

    try {
      outer: for (const [, list] of byWorkspace) {
        for (let idx = 0; idx < list.length; idx++) {
          const item = list[idx]!;
          const nowMs = this.now();
          // Waiting for its backoff: keep the order, later items of this workspace wait too.
          if (!force && item.status === 'pending' && item.nextAttemptAt && Date.parse(item.nextAttemptAt) > nowMs) break;
          let res: unknown;
          try {
            res = await this.send(item);
          } catch (err) {
            const kind = classifyReplayError(err);
            const lastError = toOutboxError(err);
            if (kind === 'offline') {
              result.stoppedBy = 'offline';
              this.setOnline(this.isOnline());
              break outer;
            }
            if (kind === 'auth') {
              result.stoppedBy = 'auth';
              this.state.set((s) => ({ ...s, authPaused: true }));
              break outer;
            }
            if (kind === 'readonly') {
              // Keep this and every later item of the workspace; try again on the next sync.
              for (const rest of list.slice(idx)) {
                await this.store.put({ ...rest, status: 'blocked', lastError });
                result.blocked++;
              }
              break;
            }
            if (kind === 'conflict') {
              await this.store.put({ ...item, status: 'conflict', body: undefined, lastError });
              result.conflicts++;
              markSynced(item);
              continue;
            }
            const attempts = item.attempts + 1;
            if (kind === 'retry' && attempts < MAX_ATTEMPTS) {
              const next = new Date(nowMs + backoffDelay(attempts, this.random)).toISOString();
              await this.store.put({ ...item, status: 'pending', attempts, nextAttemptAt: next, lastError });
              break; // keep order within this workspace
            }
            await this.store.put({ ...item, status: 'failed', attempts, nextAttemptAt: null, lastError });
            result.failed++;
            continue;
          }
          if (isConflictBody(res)) {
            // Applied as "newer version kept": the server saved this change in its history.
            await this.store.put({
              ...item,
              status: 'conflict',
              body: undefined,
              lastError: {
                status: 200,
                code: 'superseded',
                message: 'A newer change made on another device was kept. Your change was saved in the history.',
              },
            });
            result.conflicts++;
          } else {
            await this.store.delete(item.id);
            result.sent++;
          }
          markSynced(item);
        }
      }
    } finally {
      const after = await this.refresh();
      result.remaining = after.filter((i) => i.status === 'pending' || i.status === 'blocked').length;
      this.state.set((s) => ({
        ...s,
        syncing: false,
        ...(result.sent > 0 ? { lastSyncedAt: new Date(this.now()).toISOString(), lastSyncedCount: result.sent } : {}),
      }));
      if (result.stoppedBy === null) this.scheduleNextRetry(after);
      for (const [workspaceId, modules] of synced) {
        for (const l of this.listeners) {
          try {
            l({ workspaceId, modules: [...modules] });
          } catch {
            /* listener errors must not break the replay */
          }
        }
      }
    }
    return result;
  }
}

/** The app-wide outbox. */
export const offlineQueue = new Outbox();
